/**
 * Secure token storage utilities using cookies
 * Handles storage and retrieval of authentication tokens with security best practices
 */

import { AuthTokens, User } from './types';

const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const TOKEN_TYPE_KEY = 'auth_token_type';
const USER_KEY = 'auth_user';

/**
 * Check if we're in a browser environment
 */
const isBrowser = typeof window !== 'undefined';

/**
 * Cookie options for secure storage
 */
interface CookieOptions {
  maxAge?: number; // in seconds
  expires?: Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Default cookie options for authentication tokens
 */
const defaultTokenCookieOptions: CookieOptions = {
  maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
  path: '/',
  secure: process.env.NODE_ENV === 'production', // Only secure in production
  sameSite: 'lax',
  // Note: httpOnly is not used for client-side accessible cookies
};

/**
 * Cookie storage utilities
 */
class CookieStorage {
  /**
   * Set a cookie with options
   */
  setCookie(name: string, value: string, options: CookieOptions = {}): void {
    if (!isBrowser) return;

    try {
      const opts = { ...defaultTokenCookieOptions, ...options };
      let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

      if (opts.maxAge) {
        cookieString += `; Max-Age=${opts.maxAge}`;
      }

      if (opts.expires) {
        cookieString += `; Expires=${opts.expires.toUTCString()}`;
      }

      if (opts.path) {
        cookieString += `; Path=${opts.path}`;
      }

      if (opts.domain) {
        cookieString += `; Domain=${opts.domain}`;
      }

      if (opts.secure) {
        cookieString += '; Secure';
      }

      if (opts.httpOnly) {
        cookieString += '; HttpOnly';
      }

      if (opts.sameSite) {
        cookieString += `; SameSite=${opts.sameSite}`;
      }

      document.cookie = cookieString;
    } catch (error) {
      console.warn(`Failed to set cookie: ${name}`, error);
    }
  }

  /**
   * Get a cookie value by name
   */
  getCookie(name: string): string | null {
    if (!isBrowser) return null;

    try {
      const nameEQ = encodeURIComponent(name) + '=';
      const cookies = document.cookie.split(';');

      for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith(nameEQ)) {
          return decodeURIComponent(cookie.substring(nameEQ.length));
        }
      }

      return null;
    } catch (error) {
      console.warn(`Failed to get cookie: ${name}`, error);
      return null;
    }
  }

  /**
   * Delete a cookie by setting it to expire
   */
  deleteCookie(name: string, options: Partial<CookieOptions> = {}): void {
    if (!isBrowser) return;

    try {
      const opts = { ...defaultTokenCookieOptions, ...options };
      let cookieString = `${encodeURIComponent(name)}=; Max-Age=0`;

      if (opts.path) {
        cookieString += `; Path=${opts.path}`;
      }

      if (opts.domain) {
        cookieString += `; Domain=${opts.domain}`;
      }

      document.cookie = cookieString;
    } catch (error) {
      console.warn(`Failed to delete cookie: ${name}`, error);
    }
  }

  /**
   * Check if cookies are available
   */
  areCookiesAvailable(): boolean {
    if (!isBrowser) return false;

    try {
      // Check if document.cookie is accessible
      if (typeof document === 'undefined' || typeof document.cookie !== 'string') {
        return false;
      }

      // Test if we can set and get a cookie (more lightweight test)
      const testKey = '_cookie_test';
      const testValue = 'test';
      
      // Simple cookie test without using our own methods to avoid recursion
      document.cookie = `${testKey}=${testValue}; path=/; max-age=1`;
      const cookieExists = document.cookie.includes(`${testKey}=${testValue}`);
      
      // Clean up test cookie
      if (cookieExists) {
        document.cookie = `${testKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
      
      return cookieExists;
    } catch (error) {
      return false;
    }
  }
}

/**
 * In-memory storage fallback for when cookies are not available
 */
class InMemoryStorage {
  private storage = new Map<string, string>();

  getItem(key: string): string | null {
    return this.storage.get(key) || null;
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

// Initialize storage strategy
const cookieStorage = new CookieStorage();
const memoryStorage = new InMemoryStorage();

/**
 * Storage strategy that uses cookies with memory fallback
 */
class HybridStorage {
  private useCookies: boolean | null = null; // null means not yet determined
  private initialized = false;

  constructor() {
    // Don't check cookies immediately - wait for client-side
    if (isBrowser && document.readyState === 'complete') {
      this.initializeStorage();
    } else if (isBrowser) {
      // Wait for DOM to be ready
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeStorage();
      });
    }
  }

  private initializeStorage(): void {
    if (this.initialized) return;
    
    this.useCookies = cookieStorage.areCookiesAvailable();
    this.initialized = true;
    
    if (!this.useCookies) {
      console.warn('Cookies are not available, falling back to memory storage');
    }
  }

  private ensureInitialized(): void {
    if (!this.initialized && isBrowser) {
      this.initializeStorage();
    }
  }

  getItem(key: string): string | null {
    this.ensureInitialized();
    
    if (this.useCookies) {
      return cookieStorage.getCookie(key);
    }
    return memoryStorage.getItem(key);
  }

  setItem(key: string, value: string, options?: CookieOptions): void {
    this.ensureInitialized();
    
    if (this.useCookies) {
      cookieStorage.setCookie(key, value, options);
    } else {
      memoryStorage.setItem(key, value);
    }
  }

  removeItem(key: string): void {
    this.ensureInitialized();
    
    if (this.useCookies) {
      cookieStorage.deleteCookie(key);
    } else {
      memoryStorage.removeItem(key);
    }
  }

  clear(): void {
    this.ensureInitialized();
    
    if (this.useCookies) {
      // Clear auth-related cookies
      this.removeItem(ACCESS_TOKEN_KEY);
      this.removeItem(REFRESH_TOKEN_KEY);
      this.removeItem(TOKEN_TYPE_KEY);
      this.removeItem(USER_KEY);
    } else {
      memoryStorage.clear();
    }
  }
}

// Initialize hybrid storage
const storage = new HybridStorage();

/**
 * Token storage utilities using cookies
 */
export const tokenStorage = {
  /**
   * Store authentication tokens securely in cookies
   */
  setTokens(tokens: AuthTokens): void {
    const tokenOptions = {
      maxAge: 7 * 24 * 60 * 60, // 7 days
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax' as const,
    };

    const refreshTokenOptions = {
      maxAge: 30 * 24 * 60 * 60, // 30 days for refresh token
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax' as const,
    };

    storage.setItem(ACCESS_TOKEN_KEY, tokens.access_token, tokenOptions);
    storage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token, refreshTokenOptions);
    storage.setItem(TOKEN_TYPE_KEY, tokens.token_type, tokenOptions);
  },

  /**
   * Retrieve stored authentication tokens
   */
  getTokens(): AuthTokens | null {
    const accessToken = storage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = storage.getItem(REFRESH_TOKEN_KEY);
    const tokenType = storage.getItem(TOKEN_TYPE_KEY);

    if (!accessToken || !refreshToken || !tokenType) {
      return null;
    }

    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      token_type: tokenType,
    };
  },

  /**
   * Get access token only
   */
  getAccessToken(): string | null {
    return storage.getItem(ACCESS_TOKEN_KEY);
  },

  /**
   * Get refresh token only
   */
  getRefreshToken(): string | null {
    return storage.getItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Update access token (typically after refresh)
   */
  updateAccessToken(accessToken: string): void {
    const tokenOptions = {
      maxAge: 7 * 24 * 60 * 60, // 7 days
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax' as const,
    };
    storage.setItem(ACCESS_TOKEN_KEY, accessToken, tokenOptions);
  },

  /**
   * Clear all stored tokens
   */
  clearTokens(): void {
    storage.removeItem(ACCESS_TOKEN_KEY);
    storage.removeItem(REFRESH_TOKEN_KEY);
    storage.removeItem(TOKEN_TYPE_KEY);
  },

  /**
   * Store user information
   */
  setUser(user: Partial<User> | null): void {
    if(!user){
      return;
    }
    const userOptions = {
      maxAge: 7 * 24 * 60 * 60, // 7 days
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax' as const,
    };
    storage.setItem(USER_KEY, JSON.stringify(user), userOptions);
  },

  /**
   * Retrieve stored user information
   */
  getUser(): Partial<User> | null {
    const userStr = storage.getItem(USER_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.warn('Failed to parse stored user data', error);
      return null;
    }
  },

  /**
   * Clear stored user information
   */
  clearUser(): void {
    storage.removeItem(USER_KEY);
  },

  /**
   * Clear all authentication data
   */
  clearAll(): void {
    this.clearTokens();
    this.clearUser();
  },

  /**
   * Check if tokens exist in storage
   */
  hasTokens(): boolean {
    return Boolean(this.getAccessToken() && this.getRefreshToken());
  },

  /**
   * Check if access token is potentially expired (basic check)
   * This is a simple heuristic - proper validation should be done server-side
   */
  isTokenPotentiallyExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      // JWT tokens have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      // Decode the payload (second part)
      const payload = JSON.parse(atob(parts[1]));
      
      // Check if token has expiration time
      if (!payload.exp) return false;

      // Check if token is expired (with 5 minute buffer)
      const now = Math.floor(Date.now() / 1000);
      const buffer = 1 * 60; // TODO FIX IT 5 minutes
      
      return payload.exp < (now + buffer);
    } catch (error) {
      // If we can't parse the token, consider it expired
      return true;
    }
  }
};

export function decodeUserFromAccessToken(access: string): Partial<User>{
  try {
    const [, payload] = access.split('.');
    if (!payload) return {};
    const json = JSON.parse(
        decodeURIComponent(
            atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        )
    );

    const sub = json.sub as string | undefined;
    const email = (json.email as string | undefined) ?? (sub?.includes('@') ? sub : undefined);
    const name =
        (json.name as string | undefined) ??
        (email ? email.split('@')[0] : 'User');

    return {
      id: (sub as string) ?? email ?? 'unknown',
      email,
      name,
      picture: (json.picture as string | undefined) ?? '',
      verified_email: Boolean(json.email_verified),
    };
  } catch {
    return {};
  }
}

/**
 * Storage event listener for cross-tab synchronization
 * Note: Cookie changes don't trigger storage events, so we use a polling approach
 */
export const setupStorageListener = (callback: () => void): (() => void) => {
  if (!isBrowser) return () => {};

  let lastTokenState = {
    accessToken: tokenStorage.getAccessToken(),
    refreshToken: tokenStorage.getRefreshToken(),
    user: tokenStorage.getUser(),
  };

  // Poll for changes every 5 seconds
  const pollInterval = setInterval(() => {
    const currentTokenState = {
      accessToken: tokenStorage.getAccessToken(),
      refreshToken: tokenStorage.getRefreshToken(),
      user: tokenStorage.getUser(),
    };

    // Check if any auth-related data has changed
    if (
      currentTokenState.accessToken !== lastTokenState.accessToken ||
      currentTokenState.refreshToken !== lastTokenState.refreshToken ||
      JSON.stringify(currentTokenState.user) !== JSON.stringify(lastTokenState.user)
    ) {
      lastTokenState = currentTokenState;
      callback();
    }
  }, 5000);

  // Also listen for focus events (when user switches back to tab)
  const handleFocus = () => {
    const currentTokenState = {
      accessToken: tokenStorage.getAccessToken(),
      refreshToken: tokenStorage.getRefreshToken(),
      user: tokenStorage.getUser(),
    };

    if (
      currentTokenState.accessToken !== lastTokenState.accessToken ||
      currentTokenState.refreshToken !== lastTokenState.refreshToken ||
      JSON.stringify(currentTokenState.user) !== JSON.stringify(lastTokenState.user)
    ) {
      lastTokenState = currentTokenState;
      callback();
    }
  };

  window.addEventListener('focus', handleFocus);
  
  // Return cleanup function
  return () => {
    clearInterval(pollInterval);
    window.removeEventListener('focus', handleFocus);
  };
};
